import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useInterval } from 'usehooks-ts';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function isIzoAudioFile(f) {
    return (typeof f.src === 'string' &&
        typeof f.label === 'string' &&
        typeof f.failedToLoad === 'boolean' &&
        (typeof f.audioBuffer === 'object' || f.audioBuffer === undefined));
}
function isIzoTrack(f) {
    return (f.sourceNode !== undefined &&
        f.fadeNode !== undefined &&
        f.gainNode !== undefined &&
        f.muteNode !== undefined &&
        f.panNode !== undefined);
}

function decodeAudio(arrayBuffer) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield new Promise(res => {
            if (typeof window === 'undefined') {
                throw new Error('window not available. Cannot call AudioContext.decodeAudio()');
            }
            return window.izo_audio_context.decodeAudioData(arrayBuffer, buf => res(buf), e => {
                throw new Error(e + `, in decodeAudio()`);
            });
        });
    });
}
function fetchAudio(src) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield fetch(src);
            if (!res.ok) {
                console.error(`Error fetching audio file ${src}:`, res);
                return null;
            }
            return res.arrayBuffer();
        }
        catch (e) {
            console.error(e + `, in fetchAudio()`);
            return null;
        }
    });
}
function loadAudioFile(url) {
    return __awaiter(this, void 0, void 0, function* () {
        const arrayBuffer = yield fetchAudio(url);
        if (!arrayBuffer) {
            console.error('Invalid response after fetching audio from S3:', JSON.stringify(url));
            return { src: url, failedToLoad: true };
        }
        return {
            src: url,
            audioBuffer: yield decodeAudio(arrayBuffer),
            failedToLoad: false,
        };
    });
}
function createPanNode(initValue = 0) {
    if (typeof window === 'undefined')
        throw Error('window not available');
    if (window.izo_audio_context.createStereoPanner) {
        const panNode = window.izo_audio_context.createStereoPanner();
        panNode.pan.setValueAtTime(initValue, 0);
        return panNode;
    }
    else {
        const panNode = window.izo_audio_context.createPanner();
        panNode.setPosition(initValue, 0, 1 - Math.abs(initValue));
        return panNode;
    }
}
function isStereoPanner(node) {
    return node.pan !== undefined;
}
function updatePanNode(node, value) {
    if (isStereoPanner(node)) {
        node.pan.setValueAtTime(value, 0 /*window.izo_audio_context.currentTime*/);
    }
    else {
        node.setPosition(value, 0, 1 - Math.abs(value));
    }
}
let globalSumNode;
//Simplified version from T+. Creates nodes from a single buffer
function constructTracks(files, loop) {
    globalSumNode = window.izo_audio_context.createGain();
    globalSumNode.gain.value = 1.0;
    window.globalAnalyser = window.izo_audio_context.createAnalyser();
    window.globalAnalyser.fftSize = 2048;
    const tracks = [];
    for (const f of files) {
        const track = Object.assign({}, f);
        track.sourceNode = window.izo_audio_context.createBufferSource();
        track.sourceNode.buffer = f.audioBuffer || null;
        if (f.audioBuffer && loop) {
            track.sourceNode.loop = true;
            track.sourceNode.loopStart = 0;
            track.sourceNode.loopEnd = f.audioBuffer.length / f.audioBuffer.sampleRate;
        }
        track.fadeNode = window.izo_audio_context.createGain();
        track.fadeNode.gain.setValueAtTime(1, 0);
        track.gainNode = window.izo_audio_context.createGain();
        track.gainNode.gain.setValueAtTime(f.gain ? f.gain.value : 1, 0);
        track.muteNode = window.izo_audio_context.createGain();
        track.muteNode.gain.setValueAtTime(f.mute && f.mute.value ? 0 : 1, 0);
        track.panNode = createPanNode(f.pan ? f.pan.value : 0);
        track.sourceNode.connect(track.fadeNode);
        track.fadeNode.connect(track.gainNode);
        track.gainNode.connect(track.muteNode);
        track.muteNode.connect(track.panNode);
        track.panNode.connect(globalSumNode);
        if (!isIzoTrack(track)) {
            continue;
        }
        tracks.push(track);
    }
    // finally hook up the summing node
    globalSumNode.connect(window.izo_audio_context.destination);
    globalSumNode.connect(window.globalAnalyser);
    return tracks;
}
function startAudioNodes(audioNodes, elapsed) {
    for (const a of audioNodes) {
        try {
            a.sourceNode.start(0, elapsed);
        }
        catch (e) {
            console.error(`Could not start audio node`, JSON.stringify(e), e);
        }
    }
}
function updateNodeParams(updates, audioNodes) {
    audioNodes
        .filter(n => Object.keys(updates).includes(n.label))
        .forEach(n => {
        const u = updates[n.label];
        u.mute &&
            n.muteNode.gain.setValueAtTime(u.mute.value ? 0 : 1, 0 // window.izo_audio_context.currentTime
            );
        u.gain &&
            n.gainNode.gain.setValueAtTime(u.gain.value, 0 // window.izo_audio_context.currentTime
            );
        u.pan && updatePanNode(n.panNode, u.pan.value);
    });
}
function deConstructAudioNodes(audioNodes) {
    for (const a of audioNodes) {
        try {
            a.sourceNode && a.sourceNode.stop();
            a.sourceNode && a.sourceNode.disconnect();
            a.fadeNode && a.fadeNode.disconnect();
            a.gainNode && a.gainNode.disconnect();
            a.muteNode && a.muteNode.disconnect();
            a.panNode && a.panNode.disconnect();
        }
        catch (_a) { }
    }
}

const AudioReducer = (state, action) => {
    var _a, _b, _c, _d, _e, _f;
    switch (action.type) {
        case 'INITIALIZE_CONTEXT':
            return Object.assign(Object.assign({}, state), { elapsed: 0, _pausedAt: 0, _unmuteDispose: action.unmuteDispose });
        case 'LOAD_FILES':
            return Object.assign(Object.assign({}, state), { _setFilesTo: action.fileSet, filesLoading: true, filesLoaded: false });
        case 'LOAD_FILES_SUCCESS':
            const { duration, files, activeAudio } = action;
            return Object.assign(Object.assign({}, state), { _setFilesTo: null, filesLoading: false, filesLoaded: true, duration,
                files,
                activeAudio });
        case 'SET_PLAYBACK':
            return Object.assign(Object.assign({}, state), { _setPlaybackTo: action.playback });
        case 'SET_PLAYBACK_SUCCESS':
            return Object.assign(Object.assign({}, state), { _setPlaybackTo: null, playing: action.playing, elapsed: (_a = action.elapsed) !== null && _a !== void 0 ? _a : state.elapsed, _playedAt: (_b = action.playedAt) !== null && _b !== void 0 ? _b : state._playedAt, _pausedAt: (_c = action.pausedAt) !== null && _c !== void 0 ? _c : state._pausedAt });
        case 'SET_PLAYHEAD':
            return Object.assign(Object.assign({}, state), { _setElapsedTo: action.elapsed });
        case 'SET_PLAYHEAD_SUCCESS':
            return Object.assign(Object.assign({}, state), { _setElapsedTo: null, elapsed: action.elapsed, _pausedAt: action.elapsed, _playedAt: (_d = action.playedAt) !== null && _d !== void 0 ? _d : state._playedAt });
        case 'SET_ACTIVE_AUDIO':
            return Object.assign(Object.assign({}, state), { _setActiveAudioTo: action.labels });
        case 'SET_ACTIVE_AUDIO_SUCCESS':
            return Object.assign(Object.assign({}, state), { _setActiveAudioTo: null, activeAudio: action.activeAudio });
        case 'SET_PLAYBACK_PARAMS':
            return Object.assign(Object.assign({}, state), { _setPlaybackParamsTo: action.updates });
        case 'SET_PLAYBACK_PARAMS_SUCCESS':
            return Object.assign(Object.assign({}, state), { _setPlaybackParamsTo: null, files: action.files });
        //no _SUCCESS counterpart as this is not a result of consumer action
        case 'BUMP_CLOCK':
            return Object.assign(Object.assign({}, state), { elapsed: action.elapsed, _pausedAt: (_e = action.pausedAt) !== null && _e !== void 0 ? _e : state._pausedAt, playing: (_f = action.playing) !== null && _f !== void 0 ? _f : state.playing });
        default:
            return state;
    }
};

/**
 * @file unmute.ts
 * @author Spencer Evans evans.spencer@gmail.com
 *
 * unmute is a disgusting hack that helps..
 * 	1) automatically resume web audio contexts on user interaction
 * 	2) automatically pause and resume web audio when the page is hidden.
 * 	3) ios only: web audio play on the media channel rather than the ringer channel
 * 	4) ios only: disable the media playback widget and airplay when:
 *
 * WebAudio is automatically resumed by checking context state and resuming whenever possible.
 *
 * WebAudio pausing is accomplished by watching the page visilibility API. When on iOS, page focus
 * is also used to determine if the page is in the foreground because Apple's page vis api implementation is buggy.
 *
 * iOS Only: Forcing WebAudio onto the media channel (instead of the ringer channel) works by playing
 * a short, high-quality, silent html audio track continuously when web audio is playing.
 *
 * iOS Only: Hiding the media playback widgets on iOS is accomplished by completely nuking the silent
 * html audio track whenever the app isn't in the foreground.
 *
 * iOS detection is done by looking at the user agent for iPhone, iPod, iPad. This detects the phones fine, but
 * apple likes to pretend their new iPads are computers (lol right..). Newer iPads are detected by finding
 * mac osx in the user agent and then checking for touch support by testing navigator.maxTouchPoints > 0.
 *
 * This is all really gross and apple should really fix their janky browser.
 * This code isn't optimized in any fashion, it is just whipped up to help someone out on stack overflow, its just meant as an example.
 */
/**
 * Enables unmute.
 * @param context A reference to the web audio context to "unmute".
 * @param allowBackgroundPlayback Optional. Default false. Allows audio to continue to play in the background. This is not recommended because it will burn battery and display playback controls on the iOS lockscreen.
 * @param forceIOSBehavior Optional. Default false. Forces behavior to that which is on iOS. This *could* be used in the event the iOS detection fails (which it shouldn't). It is strongly recommended NOT to use this.
 * @returns An object containing a dispose function which can be used to dispose of the unmute controller.
 */
function unmute(context, allowBackgroundPlayback = false, forceIOSBehavior = false) {
    //#region Helpers
    // Determine the page visibility api
    let pageVisibilityAPI = {
        hidden: 'hidden',
        visibilitychange: 'visibilitychange',
    };
    if (document.webkitHidden !== undefined)
        pageVisibilityAPI = {
            hidden: 'webkitHidden',
            visibilitychange: 'webkitvisibilitychange',
        };
    else if (document.mozHidden !== undefined)
        pageVisibilityAPI = {
            hidden: 'mozHidden',
            visibilitychange: 'mozvisibilitychange',
        };
    else if (document.msHidden !== undefined)
        pageVisibilityAPI = {
            hidden: 'msHidden',
            visibilitychange: 'msvisibilitychange',
        };
    // Helpers to add/remove a bunch of event listeners
    function addEventListeners(target, events, handler, capture, passive) {
        for (let i = 0; i < events.length; ++i)
            target.addEventListener(events[i], handler, {
                capture: capture,
                passive: passive,
            });
    }
    function removeEventListeners(target, events, handler, capture, passive) {
        for (let i = 0; i < events.length; ++i)
            target.removeEventListener(events[i], handler, {
                capture: capture,
                passive: passive,
            });
    }
    /**
     * Helper no-operation function to ignore promises safely
     */
    function noop() { }
    //#endregion
    //#region iOS Detection
    let ua = navigator.userAgent.toLowerCase();
    let isIOS = forceIOSBehavior ||
        (ua.indexOf('iphone') >= 0 && ua.indexOf('like iphone') < 0) ||
        (ua.indexOf('ipad') >= 0 && ua.indexOf('like ipad') < 0) ||
        (ua.indexOf('ipod') >= 0 && ua.indexOf('like ipod') < 0) ||
        (ua.indexOf('mac os x') >= 0 && navigator.maxTouchPoints > 0); // New ipads show up as macs in user agent, but they have a touch screen
    //#endregion
    //#region Playback Allowed State
    /** Indicates if audio should be allowed to play. */
    let allowPlayback = true; // Assume page is visible and focused by default
    /**
     * Updates playback state.
     */
    function updatePlaybackState() {
        // Check if should be active
        let shouldAllowPlayback = allowBackgroundPlayback || // always be active if noPause is indicated
            ((!pageVisibilityAPI || !document[pageVisibilityAPI.hidden]) && // can be active if no page vis api, or page not hidden
                (!isIOS || document.hasFocus())) // if ios, then document must also be focused because their page vis api is buggy
            ? true
            : false;
        // Change state
        if (shouldAllowPlayback !== allowPlayback) {
            allowPlayback = shouldAllowPlayback;
            // Update the channel state
            updateChannelState(false);
            // The playback allowed state has changed, update the context state to suspend / resume accordingly
            updateContextState();
        }
    }
    /**
     * Handle visibility api events.
     */
    function doc_visChange() {
        updatePlaybackState();
    }
    if (pageVisibilityAPI)
        addEventListeners(document, [pageVisibilityAPI.visibilitychange], doc_visChange, true, true);
    /**
     * Handles blur events (only used on iOS because it doesn't dispatch vis change events properly).
     */
    function win_focusChange(evt) {
        if (evt && evt.target !== window)
            return; // ignore bubbles
        updatePlaybackState();
    }
    if (isIOS)
        addEventListeners(window, ['focus', 'blur'], win_focusChange, true, true);
    //#endregion
    //#region WebAudio Context State
    /**
     * Updates the context state.
     * NOTE: apple supports (and poorly at that) the proposed "interrupted" state spec, just ignore that for now.
     */
    function updateContextState() {
        if (allowPlayback) {
            // Want to be running, so try resuming if necessary
            if (context.state !== 'running' && context.state !== 'closed') {
                // do nothing if the context was closed to avoid errors... can't check for the suspended state because of apple's crappy interrupted implementation
                // Can only resume after a media playback (input) event has occurred
                if (hasMediaPlaybackEventOccurred) {
                    let p = context.resume();
                    if (p)
                        p.then(noop, noop).catch(noop);
                }
            }
        }
        else {
            // Want to be suspended, so try suspending
            if (context.state === 'running') {
                let p = context.suspend();
                if (p)
                    p.then(noop, noop).catch(noop);
            }
        }
    }
    /**
     * Handles context statechange events.
     * @param evt The event.
     */
    function context_statechange(evt) {
        // Check if the event was already handled since we're listening for it both ways
        if (!evt || !evt.unmute_handled) {
            evt.unmute_handled = true;
            // The context may have auto changed to some undesired state, so immediately check again if we want to change it
            updateContextState();
        }
    }
    addEventListeners(context, ['statechange'], context_statechange, true, true); // NOTE: IIRC some devices don't support the onstatechange event callback, so handle it both ways
    if (!context.onstatechange)
        context.onstatechange = context_statechange; // NOTE: IIRC older androids don't support the statechange event via addeventlistener, so handle it both ways
    //#endregion
    //#region HTML Audio Channel State
    /** The html audio element that forces web audio playback onto the media channel instead of the ringer channel. */
    let channelTag = null;
    /**
     * A utility function for decompressing the base64 silence string. A poor-mans implementation of huffman decoding.
     * @param count The number of times the string is repeated in the string segment.
     * @param repeatStr The string to repeat.
     * @returns The
     */
    function huffman(count, repeatStr) {
        let e = repeatStr;
        for (; count > 1; count--)
            e += repeatStr;
        return e;
    }
    /**
     * A very short bit of silence to be played with <audio>, which forces AudioContext onto the ringer channel.
     * NOTE: The silence MP3 must be high quality, when web audio sounds are played in parallel the web audio sound is mixed to match the bitrate of the html sound.
     * This file is 0.01 seconds of silence VBR220-260 Joint Stereo 859B
     * The str below is a "compressed" version using poor mans huffman encoding, saves about 0.5kb
     */
    let silence = 'data:audio/mpeg;base64,//uQx' +
        huffman(23, 'A') +
        'WGluZwAAAA8AAAACAAACcQCA' +
        huffman(16, 'gICA') +
        huffman(66, '/') +
        '8AAABhTEFNRTMuMTAwA8MAAAAAAAAAABQgJAUHQQAB9AAAAnGMHkkI' +
        huffman(320, 'A') +
        '//sQxAADgnABGiAAQBCqgCRMAAgEAH' +
        huffman(15, '/') +
        '7+n/9FTuQsQH//////2NG0jWUGlio5gLQTOtIoeR2WX////X4s9Atb/JRVCbBUpeRUq' +
        huffman(18, '/') +
        '9RUi0f2jn/+xDECgPCjAEQAABN4AAANIAAAAQVTEFNRTMuMTAw' +
        huffman(97, 'V') +
        'Q==';
    /**
     * Updates the html audio channel control.
     * @param isMediaPlaybackEvent Indicates if being called from within a media playback event handler.
     */
    function updateChannelState(isMediaPlaybackEvent) {
        // Totally unnecessary outside of iOS
        if (isIOS) {
            if (allowPlayback) {
                // We want to be playing back on the media channel, but can only do so from an allowed input event
                if (isMediaPlaybackEvent) {
                    // Create a new channel tag if necessary
                    if (!channelTag) {
                        let tmp = document.createElement('div');
                        tmp.innerHTML = "<audio x-webkit-airplay='deny'></audio>"; // Airplay like controls on other devices, prevents casting of the tag, doesn't work on modern iOS
                        channelTag = tmp.children.item(0);
                        channelTag.controls = false;
                        channelTag.disableRemotePlayback = true; // Airplay like controls on other devices, prevents casting of the tag, doesn't work on modern iOS
                        channelTag.preload = 'auto';
                        channelTag.src = silence;
                        channelTag.loop = true;
                        channelTag.load();
                    }
                    // Play the channel tag
                    if (channelTag.paused) {
                        let p = channelTag.play();
                        if (p)
                            p.then(noop, destroyChannelTag).catch(destroyChannelTag); // If playback fails the tag is pretty much trash and needs to be recreated on next media playback event
                    }
                }
            }
            else {
                // We don't want to be allowing playback at all at the moment, so destroy the channel tag to halt playback and hide those silly iOS media controls
                destroyChannelTag();
            }
        }
    }
    /**
     * Complete unloads / destroys the channel tag.
     */
    function destroyChannelTag() {
        if (channelTag) {
            // Change src to nothing and trigger a load, this is required to actually hide / clear the iOS playback controls
            channelTag.src = 'about:blank';
            channelTag.load();
            channelTag = null;
        }
    }
    //#endregion
    //#region Input
    /** The event types that can trigger media playback. */
    const mediaPlaybackEvents = [
        'click',
        'contextmenu',
        'auxclick',
        'dblclick',
        'mousedown',
        'mouseup',
        'touchend',
        'keydown',
        'keyup',
    ];
    /** Tracks if a media playback event has occurred */
    let hasMediaPlaybackEventOccurred = false;
    /**
     * Handles events that can begin media playback.
     */
    function win_mediaPlaybackEvent() {
        hasMediaPlaybackEventOccurred = true;
        // This is an opportunity to resume the html audio channel control
        updateChannelState(true);
        // This is an opportunity to resume the context if paused
        updateContextState();
    }
    addEventListeners(window, mediaPlaybackEvents, win_mediaPlaybackEvent, true, true);
    //#endregion
    return {
        /**
         * Disposes unmute, relinquishing all control of media playback.
         */
        dispose: function () {
            // Stop / clean up the channel tag
            destroyChannelTag();
            // Remove all listeners
            if (pageVisibilityAPI)
                removeEventListeners(document, [pageVisibilityAPI.visibilitychange], doc_visChange, true, true);
            if (isIOS)
                removeEventListeners(window, ['focus', 'blur'], win_focusChange, true, true);
            removeEventListeners(window, mediaPlaybackEvents, win_mediaPlaybackEvent, true, true);
            removeEventListeners(context, ['statechange'], context_statechange, true, true);
            if (context.onstatechange === context_statechange)
                context.onstatechange = null;
        },
    };
}

/**
 * A helper to create a Context and Provider with no upfront default value, and
 * without having to check for undefined all the time.
 */
function createCtx() {
    const ctx = createContext(undefined);
    function useCtx() {
        const c = useContext(ctx);
        if (c === undefined)
            throw new Error('useCtx must be inside a Provider with a value');
        return c;
    }
    return [useCtx, ctx.Provider]; // 'as const' makes TypeScript infer a tuple
}
const [useIzoAudio, IzoAudioProvider] = createCtx();
// Globals
let advancePlayheadFired;
let audioTracks = [];
const initialState = {
    files: new Array(),
    activeAudio: new Array(),
    duration: 0,
    elapsed: 0,
    playing: false,
    filesLoading: false,
    filesLoaded: false,
    _playedAt: 0,
    _pausedAt: 0,
    _setPlaybackTo: null,
    _setFilesTo: null,
    _setElapsedTo: null,
    _setActiveAudioTo: null,
    _setPlaybackParamsTo: null,
    _unmuteDispose: null,
};
const AudioProvider = ({ children, loop = false, }) => {
    if (typeof window === 'undefined')
        throw Error('window not available');
    const [state, dispatch] = useReducer(AudioReducer, initialState);
    const { files, activeAudio, duration, elapsed, playing, _playedAt, _pausedAt, _setPlaybackTo, _setFilesTo, _setElapsedTo, _setActiveAudioTo, _setPlaybackParamsTo, _unmuteDispose, } = state;
    let { filesLoading, filesLoaded } = state;
    const externalState = {
        files,
        activeAudio,
        duration,
        elapsed,
        playing,
        filesLoading,
        filesLoaded,
    };
    const advancePlayhead = () => {
        if (!loop && elapsed + 0.01 > duration) {
            // handle audio finished: reset to beginning
            deConstructAudioNodes(audioTracks);
            audioTracks = [];
            dispatch({
                type: 'BUMP_CLOCK',
                elapsed: 0,
                pausedAt: 0,
                playing: false,
            });
        }
        else {
            //Note: playhead will continue instead of resetting using
            //this elapsed value for loops
            dispatch({
                type: 'BUMP_CLOCK',
                elapsed: elapsed +
                    (window.izo_audio_context.currentTime - advancePlayheadFired),
            });
        }
        advancePlayheadFired = window.izo_audio_context.currentTime;
    };
    useInterval(() => {
        advancePlayhead();
    }, playing ? 500 : null);
    function initAudioContext(sampleRate) {
        if (window.izo_audio_context) {
            if (_unmuteDispose) {
                _unmuteDispose.dispose();
            }
            window.izo_audio_context.close();
        }
        ctxInit(sampleRate);
        window.izo_audio_context.resume();
    }
    function ctxInit(sampleRate) {
        window.izo_audio_context = new (window.AudioContext ||
            window.webkitAudioContext)({
            latencyHint: 'playback',
            sampleRate: sampleRate,
        });
        const unmuteDispose = unmute(window.izo_audio_context, true);
        dispatch({ type: 'INITIALIZE_CONTEXT', unmuteDispose });
    }
    useEffect(() => {
        ctxInit();
    }, []);
    const resumeAudioContext = useCallback(() => {
        if (!window.izo_audio_context ||
            window.izo_audio_context.state === 'closed') {
            initAudioContext();
        }
        else if (window.izo_audio_context.state !== 'running') {
            window.izo_audio_context.resume();
        }
    }, []);
    const loadFiles = (fileSet) => {
        dispatch({ type: 'LOAD_FILES', fileSet });
    };
    useEffect(() => {
        function load() {
            return __awaiter(this, void 0, void 0, function* () {
                if (_setFilesTo) {
                    const newFiles = [];
                    const activeAudio = [];
                    let durations = [];
                    yield Promise.all(_setFilesTo.map((item, index) => __awaiter(this, void 0, void 0, function* () {
                        if (!item.src) {
                            return null;
                        }
                        const file = yield loadAudioFile(item.src);
                        if (file.failedToLoad || !file.audioBuffer) {
                            console.error(`failed to load file ${item.src}`);
                            return null;
                        }
                        file.label = item.label || index.toString(); //use label if provided otherwise use load index
                        file.mute = item.mute || { value: false };
                        if (!isIzoAudioFile(file)) {
                            return null;
                        }
                        newFiles.push(file);
                        durations.push(file.audioBuffer.duration);
                        if (!item.inactive) {
                            activeAudio.push(file.label);
                        }
                    })));
                    dispatch({
                        type: 'LOAD_FILES_SUCCESS',
                        duration: Math.max(...durations),
                        files: newFiles,
                        activeAudio,
                    });
                }
            });
        }
        load();
    }, [_setFilesTo]);
    const togglePlayback = useCallback(() => {
        setPlayback(!playing);
    }, [playing]);
    const setPlayback = (playback) => {
        dispatch({ type: 'SET_PLAYBACK', playback });
    };
    useEffect(() => {
        if (_setPlaybackTo !== null) {
            resumeAudioContext();
            if (_setPlaybackTo === false) {
                deConstructAudioNodes(audioTracks);
                audioTracks = [];
                dispatch({
                    type: 'SET_PLAYBACK_SUCCESS',
                    playing: false,
                    elapsed: window.izo_audio_context.currentTime - _playedAt,
                    pausedAt: window.izo_audio_context.currentTime - _playedAt,
                });
            }
            else {
                audioTracks = constructTracks(files.filter(f => activeAudio.includes(f.label)), loop);
                startAudioNodes(audioTracks, elapsed);
                dispatch({
                    type: 'SET_PLAYBACK_SUCCESS',
                    playing: true,
                    playedAt: window.izo_audio_context.currentTime - _pausedAt,
                });
                advancePlayheadFired = window.izo_audio_context.currentTime;
            }
        }
    }, [
        _setPlaybackTo,
        playing,
        _playedAt,
        _pausedAt,
        elapsed,
        activeAudio,
        files,
        resumeAudioContext,
    ]);
    const setPlayhead = (elapsed) => {
        dispatch({ type: 'SET_PLAYHEAD', elapsed });
    };
    useEffect(() => {
        if (_setElapsedTo) {
            if (_setElapsedTo >= 0) {
                deConstructAudioNodes(audioTracks);
                audioTracks = [];
                if (playing) {
                    audioTracks = constructTracks(files.filter(f => activeAudio.includes(f.label)), loop);
                    startAudioNodes(audioTracks, _setElapsedTo);
                    dispatch({
                        type: 'SET_PLAYHEAD_SUCCESS',
                        elapsed: _setElapsedTo,
                        pausedAt: _setElapsedTo,
                        playedAt: window.izo_audio_context.currentTime - _setElapsedTo,
                    });
                }
                else {
                    dispatch({
                        type: 'SET_PLAYHEAD_SUCCESS',
                        elapsed: _setElapsedTo,
                        pausedAt: _setElapsedTo,
                    });
                }
            }
        }
    }, [_setElapsedTo, files, playing, activeAudio]);
    const setActiveAudio = (activeAudio) => {
        dispatch({ type: 'SET_ACTIVE_AUDIO', labels: activeAudio });
    };
    useEffect(() => {
        if (_setActiveAudioTo) {
            deConstructAudioNodes(audioTracks);
            audioTracks = [];
            audioTracks = constructTracks(files.filter(f => _setActiveAudioTo.includes(f.label)), loop);
            if (playing) {
                startAudioNodes(audioTracks, elapsed);
            }
            dispatch({
                type: 'SET_ACTIVE_AUDIO_SUCCESS',
                activeAudio: _setActiveAudioTo,
            });
        }
    }, [_setActiveAudioTo, playing, elapsed, files]);
    const setPlaybackParams = (updates) => {
        dispatch({ type: 'SET_PLAYBACK_PARAMS', updates });
    };
    useEffect(() => {
        if (_setPlaybackParamsTo) {
            const newFiles = [];
            const realTimeUpdates = {};
            files.forEach(f => {
                if (Object.keys(_setPlaybackParamsTo).includes(f.label)) {
                    //Select only valid params (gain, pan) from update, assign default values to missing params
                    //Syntax is unusual. See https://stackoverflow.com/questions/17781472/how-to-get-a-subset-of-a-javascript-objects-properties
                    const update = _setPlaybackParamsTo[f.label];
                    newFiles.push(Object.assign(Object.assign({}, f), update));
                    realTimeUpdates[f.label] = update;
                }
                else {
                    newFiles.push(f);
                }
            });
            if (playing) {
                updateNodeParams(realTimeUpdates, audioTracks);
            }
            dispatch({ type: 'SET_PLAYBACK_PARAMS_SUCCESS', files: newFiles });
        }
    }, [_setPlaybackParamsTo, files, playing]);
    function getByLabel(label, matchPrefix = false) {
        if (matchPrefix) {
            return files.filter(f => f.label.startsWith(label));
        }
        else {
            return files.filter(f => f.label === label);
        }
    }
    return (React.createElement(IzoAudioProvider, { value: {
            state: externalState,
            initAudioContext,
            loadFiles,
            togglePlayback,
            setPlayback,
            setPlayhead,
            setActiveAudio,
            getByLabel,
            setPlaybackParams,
        } }, children));
};

// setting defaults to mobile if window unavailable server side
const INNER_WIDTH = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 428;
function calculateClipPath(audioBuffer, waveformFidelity = 0.5, mobileThreshold = 600) {
    let startFrame = 0;
    const projectNumFrames = audioBuffer.duration * audioBuffer.sampleRate;
    const endFrame = projectNumFrames;
    const gain = 1;
    const containerWidth = INNER_WIDTH < mobileThreshold ? INNER_WIDTH - 32 : INNER_WIDTH - 128;
    // if (!endFrame) endFrame = projectNumFrames
    // if (!startFrame) startFrame = 0
    const audioLength = endFrame - startFrame;
    const numSections = Math.ceil(containerWidth * waveformFidelity * (audioLength / projectNumFrames)); // width of each "bucket" in pixels
    const numSamplesPerSection = Math.ceil(audioLength / numSections);
    const downSampledData = new Float32Array(numSections);
    const l = audioBuffer.getChannelData(0);
    let r;
    if (audioBuffer.numberOfChannels > 1)
        r = audioBuffer.getChannelData(1);
    for (let i = startFrame, j = 0, sectionIndex = 0, sectionMax = 0, step = Math.floor(numSamplesPerSection / 100); i < endFrame; i += step) {
        j += step;
        sectionMax = Math.max(r ? (l[i] + r[i]) * 0.5 : l[i], sectionMax);
        if (j >= numSamplesPerSection || i >= endFrame - (1 + step)) {
            downSampledData[sectionIndex] = sectionMax * gain;
            sectionIndex++;
            sectionMax = 0;
            j = 0;
        }
    }
    const clipPath = amplitudeArrayToCSSClipPath(downSampledData.filter(value => !Number.isNaN(value)));
    return clipPath;
}
function amplitudeArrayToCSSClipPath(data) {
    let path = '0% 50%,';
    for (let i = 0; i < data.length; i++) {
        const x = (i * 100) / data.length;
        const y_pos = x === 0 ? 50 : 50 - data[i] * 50;
        const y_neg = x === 0 ? 50 : 50 + data[i] * 50;
        path =
            `${x.toFixed(2)}% ${y_neg.toFixed(2)}%,` +
                path +
                `${x.toFixed(2)}% ${y_pos.toFixed(2)}%,`;
    }
    return path.length < 8 ? '' : `polygon(100% 50%,${path}100% 50%)`; //always return a string
}

export { AudioProvider, IzoAudioProvider, calculateClipPath, isIzoAudioFile, isIzoTrack, useIzoAudio };
